import { useState } from "react";
import PayExpenseModal from "@/pages/admin/contabilidad/components/PayExpenseModal";
import PurchaseToStockModal from "@/pages/admin/contabilidad/components/PurchaseToStockModal";
import type { FundsSummary } from "@/utils/accounting";
import type { PurchaseToStockInput } from "@/store/AppStore";
import type { Expense, ExpenseStatus, FundKey, Product } from "@/types";
import { formatCOP, formatDate } from "@/utils/format";

const STATUS_META: Record<ExpenseStatus, { label: string; cls: string; icon: string }> = {
  pendiente: { label: "Pendiente", cls: "bg-secondary-500/20 text-secondary-100", icon: "ri-time-line" },
  parcial: { label: "Pago parcial", cls: "bg-accent-500/20 text-accent-200", icon: "ri-loader-4-line" },
  pagado: { label: "Pagado", cls: "bg-primary-500/25 text-primary-100", icon: "ri-checkbox-circle-line" },
};

interface PurchasesPanelProps {
  purchases: Expense[];
  products: Product[];
  funds: FundsSummary;
  onPayFromFund: (id: string, fund: FundKey, amount: number) => void;
  onPayWithLoan: (id: string, amount: number) => void;
  onPurchase: (input: PurchaseToStockInput) => void;
  onDelete: (id: string) => void;
}

// Compras de inventario / stock separadas de los gastos operativos, con estado
// de pago y acciones para pagarlas desde los fondos o con préstamo de Juank.
const PurchasesPanel = ({
  purchases,
  products,
  funds,
  onPayFromFund,
  onPayWithLoan,
  onPurchase,
  onDelete,
}: PurchasesPanelProps) => {
  const [payFor, setPayFor] = useState<Expense | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [buyOpen, setBuyOpen] = useState(false);

  const total = purchases.reduce((a, e) => a + e.amount, 0);
  const pending = purchases
    .filter((e) => e.status !== "pagado")
    .reduce((a, e) => a + Math.max(0, e.amount - e.paidAmount), 0);

  const list = [...purchases].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="glass-panel rounded-lg">
      <div className="px-5 py-4 border-b border-accent-500/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-md bg-secondary-500/25 text-secondary-200 flex items-center justify-center">
            <i className="ri-shopping-cart-2-line" />
          </span>
          <div>
            <h3 className="editable-title text-base font-bold">Compras de inventario / stock</h3>
            <p className="text-[11px] text-foreground-500">
              Total {formatCOP(total)} · por pagar{" "}
              <span className="font-semibold text-secondary-200">{formatCOP(pending)}</span>
            </p>
          </div>
        </div>
        <button
          onClick={() => setBuyOpen(true)}
          className="px-3.5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 shrink-0"
        >
          <i className="ri-add-line" /> Comprar para stock
        </button>
      </div>

      {list.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
            <i className="ri-shopping-cart-2-line text-2xl" />
          </span>
          <p className="text-sm text-foreground-700 font-semibold">No hay compras registradas</p>
          <p className="text-xs text-foreground-500 mt-1">
            Las compras por encargo, consignación o ventas a asesores aparecen aquí para pagarlas.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-background-300/25">
          {list.map((e) => {
            const meta = STATUS_META[e.status];
            const remaining = Math.max(0, e.amount - e.paidAmount);
            return (
              <li key={e.id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground-950 break-words">{e.concept}</p>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>
                      <i className={meta.icon} /> {meta.label}
                    </span>
                  </div>
                  <p className="text-[10px] text-foreground-500 mt-0.5">
                    {e.wallet} · {formatDate(e.date)}
                    {e.paidAmount > 0 && e.status !== "pagado"
                      ? ` · pagado ${formatCOP(e.paidAmount)}`
                      : ""}
                  </p>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-bold text-foreground-950 whitespace-nowrap">
                      {formatCOP(e.amount)}
                    </p>
                    {e.status !== "pagado" && (
                      <p className="text-[10px] text-secondary-200 whitespace-nowrap">
                        saldo {formatCOP(remaining)}
                      </p>
                    )}
                  </div>
                  {e.status !== "pagado" && (
                    <button
                      onClick={() => setPayFor(e)}
                      className="px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <i className="ri-secure-payment-line" /> Pagar
                    </button>
                  )}
                  {confirmId === e.id ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => {
                          onDelete(e.id);
                          setConfirmId(null);
                        }}
                        className="px-2.5 py-1 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                      >
                        Sí
                      </button>
                      <button
                        onClick={() => setConfirmId(null)}
                        className="px-2 py-1 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmId(e.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                      aria-label="Eliminar compra"
                      title="Eliminar compra"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <PayExpenseModal
        open={payFor !== null}
        expense={payFor}
        funds={funds}
        onClose={() => setPayFor(null)}
        onPayFromFund={(id, fund, amount) => {
          onPayFromFund(id, fund, amount);
          setPayFor(null);
        }}
        onPayWithLoan={(id, amount) => {
          onPayWithLoan(id, amount);
          setPayFor(null);
        }}
      />

      <PurchaseToStockModal
        open={buyOpen}
        products={products}
        cajaBalance={funds.funds.find((f) => f.key === "caja")?.balance ?? 0}
        onClose={() => setBuyOpen(false)}
        onSubmit={(input) => {
          onPurchase(input);
          setBuyOpen(false);
        }}
      />
    </div>
  );
};

export default PurchasesPanel;